import React, { useState, useRef, useEffect } from "react";
import styles from "./index.module.css";
import Modal from "components/common/Modal";

import book from "assets/svg/contact/book.svg";
import contents from "assets/svg/contact/contents.svg";
import email from "assets/svg/contact/email.svg";
import location from "assets/svg/contact/location.svg";
import nameIcon from "assets/svg/contact/name.svg";
import tel from "assets/svg/contact/tel.svg";
import upload from "assets/svg/contact/upload.svg";
import privacyFile from "assets/files/privacy.txt";

const WEBHOOK_URL = process.env.REACT_APP_DISCORD_WEBHOOK_URL;

export default function ContactTerm() {
    const [address, setAddress] = useState("");
    const [addressDetail, setAddressDetail] = useState("");
    const [nameVal, setNameVal] = useState("");
    const [phone, setPhone] = useState("");
    const [emailVal, setEmailVal] = useState("");
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [content, setContent] = useState("");
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState("선택된 파일 없음");

    const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
    const [privacyContent, setPrivacyContent] = useState("");
    const [isSuccessOpen, setIsSuccessOpen] = useState(false);

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const fileInputRef = useRef(null);

    // 개인정보 처리방침 텍스트 로드
    useEffect(() => {
        fetch(privacyFile)
            .then((res) => res.text())
            .then((data) => setPrivacyContent(data));
    }, []);

    // 카카오(Daum) 주소 검색
    const openAddressSearch = () => {
        const run = () => {
            new window.daum.Postcode({
                oncomplete: (data) => {
                    setAddress(data.roadAddress || data.jibunAddress);
                    setErrors((prev) => ({ ...prev, address: undefined }));
                },
            }).open();
        };

        if (window.daum?.Postcode) {
            run();
        } else {
            const script = document.createElement("script");
            script.src = "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
            script.onload = run;
            document.head.appendChild(script);
        }
    };

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            setFile(selected);
            setFileName(selected.name);
        } else {
            setFile(null);
            setFileName("선택된 파일 없음");
        }
    };

    const validate = () => {
        const errs = {};
        if (!address) errs.address = "주소를 검색해 주세요.";
        if (!nameVal.trim()) errs.name = "성함 또는 기업명을 입력해 주세요.";
        if (!phone.trim()) {
            errs.phone = "연락처를 입력해 주세요.";
        } else if (!/^\d{8,12}$/.test(phone.replace(/[\s-]/g, ""))) {
            errs.phone = "올바른 연락처 형식이 아닙니다.";
        }
        if (!content.trim()) errs.content = "문의 내용을 입력해 주세요.";
        if (!privacyAgreed) errs.privacy = "개인정보 처리방침에 동의해 주세요.";
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setErrors({});
        setSubmitError("");
        setIsSubmitting(true);

        try {
            const fullAddress = addressDetail.trim()
                ? `${address} (${addressDetail.trim()})`
                : address;

            const embedFields = [
                { name: "📍 주소", value: fullAddress, inline: false },
                { name: "👤 성함/기업명", value: nameVal, inline: true },
                { name: "📞 연락처", value: phone, inline: true },
                ...(emailVal.trim()
                    ? [{ name: "✉️ 이메일", value: emailVal, inline: true }]
                    : []),
                { name: "💬 문의 내용", value: content, inline: false },
            ];

            const payload = {
                embeds: [{
                    title: "📬 새 문의가 접수되었습니다",
                    color: 0x50BC95,
                    fields: embedFields,
                    timestamp: new Date().toISOString(),
                    footer: { text: "Capl 홈페이지 문의" },
                }],
            };

            const formData = new FormData();
            formData.append("payload_json", JSON.stringify(payload));
            if (file) {
                formData.append("files[0]", file, file.name);
            }

            const res = await fetch(WEBHOOK_URL, {
                method: "POST",
                body: formData,
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            // 폼 초기화
            setAddress("");
            setAddressDetail("");
            setNameVal("");
            setPhone("");
            setEmailVal("");
            setPrivacyAgreed(false);
            setContent("");
            setFile(null);
            setFileName("선택된 파일 없음");
            setIsSuccessOpen(true);
        } catch {
            setSubmitError("문의 전송에 실패했습니다. 잠시 후 다시 시도해 주세요.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.contactWrapper}>
            {/* 제목 */}
            <h2 className={styles.title}>
                <span className={styles.emphasis}>스마트 AI 주차 솔루션 <br /></span>
                <span className={styles.highlight}>Capl</span>과{" "}
                <span className={styles.highlight}>더 나은 경험</span>을 체험 해보세요
            </h2>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
                {/* 주소 */}
                <label className={styles.label}>
                    지역 <span className={styles.redPoint}>*</span>
                </label>
                <div className={`${styles.fieldRow} ${styles.compBg}`}>
                    <div className={styles.fieldRowDetail}>
                        <img className={styles.imgs} src={location} alt="location" />
                        <input
                            type="text"
                            value={address}
                            readOnly
                            placeholder="주소 검색 버튼을 눌러주세요"
                        />
                    </div>
                    <button type="button" className={styles.btnOutline} onClick={openAddressSearch}>
                        주소 검색
                    </button>
                    <input
                        type="text"
                        value={addressDetail}
                        onChange={(e) => setAddressDetail(e.target.value)}
                        placeholder="상세 주소를 입력해 주세요."
                    />
                </div>
                {errors.address && <span className={styles.errorMsg}>{errors.address}</span>}

                {/* 성함/기업명 + 연락처 */}
                <div className={styles.fieldRow}>
                    <div className={styles.fieldCol}>
                        <label className={styles.label}>
                            성함/기업명 <span className={styles.redPoint}>*</span>
                        </label>
                        <div className={`${styles.fieldRowDetail} ${styles.compBg}`}>
                            <img className={styles.imgs} src={nameIcon} alt="name" />
                            <input
                                type="text"
                                value={nameVal}
                                onChange={(e) => setNameVal(e.target.value)}
                                placeholder="성함 또는 기업명을 입력하세요."
                            />
                        </div>
                        {errors.name && <span className={styles.errorMsg}>{errors.name}</span>}
                    </div>
                    <div className={styles.fieldCol}>
                        <label className={styles.label}>
                            연락처 <span className={styles.redPoint}>*</span>
                        </label>
                        <div className={`${styles.fieldRowDetail} ${styles.compBg}`}>
                            <img className={styles.imgs} src={tel} alt="tel" />
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="연락처를 입력하세요. (ex: 01012345678)"
                            />
                        </div>
                        {errors.phone && <span className={styles.errorMsg}>{errors.phone}</span>}
                    </div>
                </div>

                {/* 이메일 + 내용 */}
                <div className={styles.fieldRow}>
                    <div className={styles.fieldCol}>
                        <label className={styles.label}>이메일</label>
                        <div className={`${styles.fieldRowDetail} ${styles.compBg}`}>
                            <img className={styles.imgs} src={email} alt="email" />
                            <input
                                type="email"
                                value={emailVal}
                                onChange={(e) => setEmailVal(e.target.value)}
                                placeholder="이메일을 입력하세요. (ex: Capl@capl.com)"
                            />
                        </div>

                        <label className={styles.label}>
                            개인정보 처리방침 <span className={styles.redPoint}>*</span>
                        </label>
                        <div className={`${styles.fieldRowDetail} ${styles.compBg} ${styles.privacyRow}`}>
                            <img className={styles.imgs} src={book} alt="book" />
                            <input
                                type="checkbox"
                                id="privacyCheck"
                                checked={privacyAgreed}
                                onChange={(e) => setPrivacyAgreed(e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <label htmlFor="privacyCheck" className={styles.checkboxLabel}>
                                개인정보처리방침에 동의합니다
                            </label>
                            <button
                                type="button"
                                className={styles.btnOutline}
                                onClick={() => setIsPrivacyOpen(true)}
                            >
                                약관 확인하기
                            </button>
                        </div>
                        {errors.privacy && <span className={styles.errorMsg}>{errors.privacy}</span>}
                    </div>
                    <div className={styles.fieldCol}>
                        <label className={styles.label}>
                            내용 <span className={styles.redPoint}>*</span>
                        </label>
                        <div className={`${styles.fieldRowFS} ${styles.compBg}`}>
                            <img className={styles.imgsT} src={contents} alt="contents" />
                            <textarea
                                className={styles.textSection}
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="문의내용을 입력하세요"
                            />
                        </div>
                        {errors.content && <span className={styles.errorMsg}>{errors.content}</span>}
                    </div>
                </div>

                {/* 파일 업로드 */}
                <label className={styles.label}>파일 업로드</label>
                <div className={`${styles.fieldRow} ${styles.compBg} ${styles.displayFiles}`}>
                    <div className={styles.fieldRowDetail}>
                        <img className={styles.imgs} src={upload} alt="upload" />
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className={styles.fileInput}
                        />
                        <p>{fileName}</p>
                    </div>
                    <button
                        type="button"
                        className={styles.btnOutline}
                        onClick={() => fileInputRef.current.click()}
                    >
                        파일 업로드
                    </button>
                </div>

                {submitError && <p className={styles.submitError}>{submitError}</p>}

                {/* 제출 */}
                <div className={styles.submitWrap}>
                    <button
                        type="submit"
                        className={styles.submitBtn}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "전송 중..." : "문의하기"}
                    </button>
                </div>
            </form>

            {/* 개인정보 처리방침 모달 */}
            <Modal
                isOpen={isPrivacyOpen}
                onClose={() => setIsPrivacyOpen(false)}
                contentStyle={{ width: "min(700px, 90vw)", textAlign: "left" }}
            >
                <h3 style={{ marginTop: 0, fontFamily: "PPLG7" }}>개인정보 처리방침</h3>
                <div style={{
                    maxHeight: "55vh",
                    overflowY: "auto",
                    border: "2px solid #50BC95",
                    borderRadius: "4px",
                    marginBottom: "8px",
                }}>
                    <pre style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: "PPLG5",
                        fontSize: "13px",
                        lineHeight: 1.6,
                        padding: "16px",
                        margin: 0,
                    }}>
                        {privacyContent}
                    </pre>
                </div>
            </Modal>

            {/* 제출 성공 모달 */}
            <Modal isOpen={isSuccessOpen} onClose={() => setIsSuccessOpen(false)}>
                <p style={{ fontFamily: "PPLG7", fontSize: "18px", margin: "0 0 8px" }}>
                    ✅ 문의가 접수되었습니다!
                </p>
                <p style={{ fontFamily: "PPLG5", color: "#777", margin: 0 }}>
                    빠른 시일 내에 연락드리겠습니다.
                </p>
            </Modal>
        </div>
    );
}
